import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuotesPageHeader from './components/QuotesPageHeader';
import RateCalculatorModal from './components/RateCalculatorModal';
import { type QuotesSearchForm } from './components/QuotesMainGrid';
import QuotesTable, { type QuotesViewMode } from './components/QuotesTable';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import QuoteLaneEditorDrawer from './components/QuoteLaneEditorDrawer';
import CustomFilterDialog, { summarizeFilter } from './components/CustomFilterDialog';
import QuotesActiveFilterBanner from './components/QuotesActiveFilterBanner';
import CustomSearchDialog, { describeSearchBlob } from './components/CustomSearchDialog';
import { useQuoteFieldDefinitions } from '../../hooks/useQuoteFieldDefinitions';
import quoteService, { type GroupedSearchParams } from '../../services/quote.service';
import userSettingsService from '../../services/userSettings.service';
import type { QuoteGroup, QuoteListRow } from './fixtures';

/** localStorage key persisting the user's Grouped|Flat view-mode choice. */
const VIEW_MODE_KEY = 'quotes.viewMode';

/** user-settings key persisting the flat-view dynamic columns the user added, so
 *  the Quotes view reloads just as they left it (like the dispatch/LC dashboards
 *  persist their column choices). Stored as a string[] of field names. */
const FLAT_COLUMNS_SETTING_KEY = 'quotes_flat_columns';
/** Persists the Custom Search "Search Ended" toggle so it reopens as last used.
 *  Stored as { ended: boolean } (the user-settings API body must be an object). */
const SEARCH_ENDED_SETTING_KEY = 'quotes_search_ended';
/** Persisted flat-view "Show Ended" header toggle — { ended: boolean }, loaded
 *  on mount so the page reopens as the user left it (no re-click every visit). */
const SHOW_ENDED_SETTING_KEY = 'quotes_show_ended';

/** join_keys whose value is single-valued per quote and supported as a flat-view
 *  dynamic column by the backend (mirrors quotes.py `_QUOTE_COLUMN_JOIN_KEYS`).
 *  Excludes 1:many (notes/charges) and the synthetic quote_search_* duplicates. */
const SUPPORTED_QUOTE_COLUMN_JOIN_KEYS = new Set<string>([
  'quote_master', 'quote_revision', 'quote_customer', 'quote_transload',
  'quote_direct', 'quote_lane', 'quote_delivery', 'quote_billto',
  // Unified lane columns: show the DIRECT or WAREHOUSE lane's value per quote
  // type (backend resolves each UNION half — see _quote_extra_select).
  'quote_search_delcity', 'quote_search_delstate', 'quote_search_delzip',
  'quote_search_deladdr1', 'quote_search_delname', 'quote_search_fueltype',
]);

// Quote fields already shown as FIXED columns in the flat grid — excluded from the
// dynamic column picker so it never offers a duplicate of an always-present column
// (the source of the "Customer / Bill-To / Sold By / Create Date appear twice"
// duplication). Those fixed columns keep their custom renderers (Quote # link,
// Status badge, computed Total, etc.); the picker only offers genuinely-addable fields.
const QUOTE_FLAT_FIXED_FIELDNAMES = new Set<string>([
  'quote_quotenumber',    // Quote #
  'quote_customername',   // Customer
  'quote_billtoname',     // Bill-To
  'quote_csr',            // CSR
  'quote_status',         // Status
  'quote_soldby',         // Sold By
  'quote_createdatetime', // Create Date
  'quote_drayrate',       // Dray
]);

/** Read the persisted view mode (default 'grouped'); guards a hostile/empty store. */
function loadViewMode(): QuotesViewMode {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === 'flat' ? 'flat' : 'grouped';
  } catch {
    return 'grouped';
  }
}

const DEFAULT_FORM: QuotesSearchForm = {
  searchFor: '',
  searchBy: 'quote_number',
  revisions: 'current',
  includeFuture: false,
  showEnded: false,
  quoteType: 'ALL',
  view: 'delivery',
  effectiveFrom: '',
  effectiveTo: '',
  status: 'ALL',
};

/**
 * Cross-navigation cache of the last committed search — the form criteria, the
 * active custom-filter blob, and the fetched results. QuotesPage UNMOUNTS when
 * the user opens an account/quote detail (a route change); on return it
 * remounts. Without this, the mount effect would re-run the DEFAULT search and
 * the user's criteria + results would be lost. Held in-memory, so it survives
 * tab-session navigation and a full page reload starts fresh.
 */
interface QuotesSearchCache {
  form: QuotesSearchForm;
  customFilter: string;
  customFilterName?: string;
  customSearch: string;
  groups: QuoteGroup[];
  totalGroups: number;
  additionalFields: string[];
}
let searchCache: QuotesSearchCache | null = null;
/** Test hook: clear the cross-navigation cache between renders. */
export function __resetQuotesSearchCache(): void { searchCache = null; }

/**
 * Quotes Main Menu page. /order-management/quotes.
 *
 * Owns the search form state and the fetched groups. On mount and on every
 * Search click, calls quoteService.searchQuotesGrouped({ all: true }) — the
 * GROUPED-display mode of /api/v1/quotes/search (group_by=customer_billto),
 * which buckets the result by (customer, bill-to). With `all: true` the backend
 * returns EVERY matching group in one request (group_all=true), so the grid
 * does sort / column-filter / global-search / pagination CLIENT-side over the
 * full set — there is no server group paging or server sort here anymore. The
 * search form / custom-filter dialogs are what narrow the set (the request);
 * everything within that set is handled in the grid. Reset clears the form back
 * to defaults and re-runs.
 *
 * Payload note: `{ all: true }` is bounded by the search form — the default
 * (current revisions) and any entered criteria narrow the result. The unfiltered
 * default load returns all current-revision groups in one call; this is the
 * accepted trade for full client-side grid features.
 *
 * The N48 single-result auto-open (an explicit search yielding exactly one
 * quote opens it directly) still applies: a single group with a single lane
 * opens that lane's quote.
 */
const QuotesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);

  // Seed from the cross-navigation cache when returning from a detail page, so
  // criteria + results are present on the first render (no flash / re-fetch).
  const [form, setForm] = useState<QuotesSearchForm>(() => searchCache?.form ?? DEFAULT_FORM);
  const [groups, setGroups] = useState<QuoteGroup[]>(() => searchCache?.groups ?? []);
  const [totalGroups, setTotalGroups] = useState(() => searchCache?.totalGroups ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Custom Filter (JSON FilterCondition[]) and Custom Search (one-field multi-value
  // blob) are INDEPENDENT — either, both, or neither can be active. The backend
  // ANDs them, so a user can search a set then filter within it (or vice versa).
  const [customFilter, setCustomFilter] = useState(() => searchCache?.customFilter ?? '');
  // Saved-filter name when the active filter came from one (drives the banner label).
  const [customFilterName, setCustomFilterName] = useState<string | undefined>(() => searchCache?.customFilterName);
  const [customSearch, setCustomSearch] = useState(() => searchCache?.customSearch ?? '');
  // Custom Search "Search Ended" toggle — persisted to user settings, loaded on
  // mount, and applied as showEnded when running a custom search.
  const [searchEnded, setSearchEnded] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Flat-view dynamic columns the user has added (field names). Seeded from the
  // cross-navigation cache; otherwise TanTable loads its persisted set on mount
  // and syncs it here via onAdditionalFieldsChange (triggering a refetch). They
  // ride in the grouped query's lanes (which feed the flat grid).
  const [additionalFields, setAdditionalFields] = useState<string[]>(() => searchCache?.additionalFields ?? []);

  // Whether the persisted flat-view dynamic-column list (FLAT_COLUMNS_SETTING_KEY)
  // has resolved. The flat grid must not mount until it has, or it applies its
  // saved layout (table_quotes-flat-grid) while the dynamic columns don't exist
  // yet — dropping their saved order/width/visibility and then autosaving the
  // degraded layout (wiping the user's setup on a cold browser). On a returning
  // navigation the cross-nav cache already holds the fields, so it's ready at once.
  const [flatFieldsLoaded, setFlatFieldsLoaded] = useState<boolean>(() => searchCache != null);

  // Grouped|Flat presentation toggle — persisted per user (localStorage),
  // default Grouped. (handleViewModeChange is defined after runSearch — it
  // refetches on switch so the Accounts view loads expired quotes.)
  const [viewMode, setViewMode] = useState<QuotesViewMode>(loadViewMode);

  // "Active Only" (Accounts view): ON by default → loads active accounts only
  // (showEnded=false); OFF → loads all accounts incl. fully-expired. Server-side
  // (drives the grouped view's showEnded) because client-side filtering isn't
  // viable — there are ~9k total accounts vs only ~500 active, and the load is
  // capped at 500 groups. (handleActiveOnlyChange is defined after runSearch.)
  const [activeOnly, setActiveOnly] = useState(true);

  // FLAT data = every loaded group's lanes flattened to one row per quote. No
  // extra fetch — the grouped response already carries every group's lanes.
  const flatLanes = useMemo<QuoteListRow[]>(() => groups.flatMap((g) => g.lanes), [groups]);

  // The lane whose editor drawer is open ON this page (FLAT view-icon opens it
  // in place; null = closed). Grouped view-icon still navigates instead.
  const [openLane, setOpenLane] = useState<QuoteListRow | null>(null);

  // FLAT view-icon handler. The editor drawer edits DIRECT and WAREHOUSE quotes
  // in place (mirrors the grouped detail page); any other type still NAVIGATES
  // to the full single-quote editor, which is the only surface that renders it.
  const handleOpenQuote = useCallback((lane: QuoteListRow) => {
    if (lane.quoteType === 'DIRECT' || lane.quoteType === 'WAREHOUSE') setOpenLane(lane);
    else navigate(`/order-management/quotes/${encodeURIComponent(lane.quoteId)}`);
  }, [navigate]);

  const runSearch = useCallback(async (overrides: Partial<{ form: QuotesSearchForm; autoOpen: boolean; customFilter: string; customSearch: string; customFilterName: string | undefined; additionalFields: string[]; viewMode: QuotesViewMode; activeOnly: boolean; showEnded: boolean }> = {}) => {

    const f = overrides.form ?? form;
    // Accounts (grouped) view: "Active Only" ON (default) loads active accounts
    // only (showEnded=false); OFF loads all incl. fully-expired. The flat (Quotes)
    // view honors its own Show Ended toggle. Server-side so the 500-group load cap
    // isn't spent on the ~9k inactive accounts. (view/toggle switches refetch only
    // when this flips — see handleViewModeChange / handleActiveOnlyChange.)
    const vm = overrides.viewMode ?? viewMode;
    const effActiveOnly = overrides.activeOnly ?? activeOnly;
    // An explicit override (the Custom Search "Search Ended" toggle) wins; else
    // the view's normal rule (grouped = Active Only; flat = Show Ended toggle).
    const effectiveShowEnded = overrides.showEnded !== undefined
      ? overrides.showEnded
      : (vm === 'grouped' ? !effActiveOnly : f.showEnded);
    const autoOpen = overrides.autoOpen ?? false;
    const cf = overrides.customFilter !== undefined ? overrides.customFilter : customFilter;
    const cs = overrides.customSearch !== undefined ? overrides.customSearch : customSearch;
    // 'in' so an explicit `customFilterName: undefined` (clearing) is respected.
    const cfName = 'customFilterName' in overrides ? overrides.customFilterName : customFilterName;
    const af = overrides.additionalFields ?? additionalFields;
    setLoading(true);
    setError(null);
    try {
      const params: GroupedSearchParams = {
        searchFor: f.searchFor || undefined,
        searchBy: f.searchBy,
        revisions: f.revisions,
        // "Include Future": add future-dated (not-yet-current) revisions on top
        // of the current ones. Server-side no-op when revisions='all'.
        includeFuture: f.includeFuture,
        // The Show Ended toggle governs uniformly: a custom search / filter honors
        // it the same as plain browsing. (Previously a custom filter/search forced
        // ended quotes in regardless of the toggle, which surprised users — e.g. a
        // "Delivery City = X" filter returned EXPIRED quotes with the toggle off.)
        // Accounts view forces this on (see effectiveShowEnded above).
        showEnded: effectiveShowEnded,
        quoteType: f.quoteType,
        view: f.view,
        effectiveFrom: f.effectiveFrom || undefined,
        effectiveTo: f.effectiveTo || undefined,
        status: f.status,
        customFilter: cf || undefined,
        customSearch: cs || undefined,
        // Flat-view dynamic columns: extra fields added to the grouped query's
        // lanes (which feed the flat grid). Omitted when none are selected.
        fields: af.length ? af.join(',') : undefined,
        // Load EVERY matching group in one request — the grid sorts / filters /
        // searches / pages client-side over the full set.
        all: true,
      };
      const resp = await quoteService.searchQuotesGrouped(params);
      // Cache this committed search (criteria + custom filter + results) so a
      // round-trip to an account/quote detail and back restores it (see mount).
      searchCache = { form: f, customFilter: cf, customFilterName: cfName, customSearch: cs, groups: resp.groups, totalGroups: resp.totalGroups, additionalFields: af };
      // N48 (search_quote.pl:1174-1182): an explicit search yielding exactly one
      // quote opens it directly. In grouped mode that's a single group holding a
      // single lane. Never on mount/reset (autoOpen=false), so FastCMS keeps its
      // paginated-list-on-mount (N45 divergence, documented).
      if (autoOpen && resp.totalGroups === 1 && resp.groups.length === 1 && resp.groups[0].lanes.length === 1) {
        navigate(`/order-management/quotes/${encodeURIComponent(resp.groups[0].lanes[0].quoteId)}`);
        return;
      }
      setGroups(resp.groups);
      setTotalGroups(resp.totalGroups);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setGroups([]);
      setTotalGroups(0);
    } finally {
      setLoading(false);
    }
  }, [form, navigate, customFilter, customSearch, customFilterName, additionalFields, viewMode, activeOnly]);

  // Whether the current dataset includes expired quotes, per view: the Accounts
  // view is driven by "Active Only" (OFF = expired loaded); the flat view by its
  // Show Ended toggle.
  const expiredLoadedFor = useCallback(
    (mode: QuotesViewMode) => (mode === 'grouped' ? !activeOnly : form.showEnded),
    [activeOnly, form.showEnded],
  );

  // Switch Accounts|Flat. Refetch only when the switch flips whether expired are
  // in the dataset (the two views can require different expired-inclusion).
  const handleViewModeChange = useCallback((mode: QuotesViewMode) => {
    const prev = viewMode;
    setViewMode(mode);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* non-fatal */ }
    if (expiredLoadedFor(prev) !== expiredLoadedFor(mode)) void runSearch({ viewMode: mode });
  }, [viewMode, expiredLoadedFor, runSearch]);

  // "Active Only" toggle (Accounts view): server-side — ON loads active accounts
  // only, OFF loads all incl. fully-expired. Always refetches (the toggle exists
  // only in the grouped view, where it always changes expired-inclusion).
  const handleActiveOnlyChange = useCallback((on: boolean) => {
    setActiveOnly(on);
    void runSearch({ activeOnly: on });
  }, [runSearch]);

  // Initial load on mount. We intentionally run this once with the defaults
  // — subsequent searches are user-triggered.
  useEffect(() => {
    // N51: an inbound Salesforce deep-link (?sfquote=Q-R) opens that quote directly.
    const sf = searchParams.get('sfquote');
    if (sf) { navigate(`/order-management/quotes/${encodeURIComponent(sf)}`); return; }
    // Returning from an account/quote detail: the state initializers already
    // restored the cached criteria + results (incl. additionalFields), so skip
    // the default re-search.
    if (searchCache) return;
    // Fresh load: restore the user's saved flat-view columns first, then run the
    // initial search WITH them so they're populated in one fetch (no flash).
    let cancelled = false;
    (async () => {
      let saved: string[] = [];
      try {
        const s = await userSettingsService.getUserSetting(FLAT_COLUMNS_SETTING_KEY);
        // Stored as { fields: string[] } — the user-settings API requires an
        // object body (a bare array is rejected).
        const fields = s?.setting_value?.fields;
        if (Array.isArray(fields)) saved = fields as string[];
      } catch { /* non-fatal — fall back to base columns */ }
      // Restore the Custom Search "Search Ended" toggle so the dialog reopens as left.
      try {
        const se = await userSettingsService.getUserSetting(SEARCH_ENDED_SETTING_KEY);
        if (!cancelled && typeof se?.setting_value?.ended === 'boolean') setSearchEnded(se.setting_value.ended);
      } catch { /* non-fatal — defaults to off */ }
      // Restore the flat-view "Show Ended" header toggle and fold it into the
      // initial search, so ended quotes are included from the first fetch.
      let initialForm = DEFAULT_FORM;
      try {
        const sh = await userSettingsService.getUserSetting(SHOW_ENDED_SETTING_KEY);
        if (typeof sh?.setting_value?.ended === 'boolean') {
          initialForm = { ...DEFAULT_FORM, showEnded: sh.setting_value.ended };
        }
      } catch { /* non-fatal — defaults to off */ }
      if (cancelled) return;
      if (initialForm !== DEFAULT_FORM) setForm(initialForm);
      if (saved.length) setAdditionalFields(saved);
      // Unblock the flat grid now that the dynamic-column list is known — set in
      // the same tick as additionalFields so the grid mounts with every column
      // present (see flatFieldsLoaded). Set even on an empty/failed load so a user
      // with no dynamic columns isn't stuck on the placeholder.
      setFlatFieldsLoaded(true);
      void runSearch({ form: initialForm, additionalFields: saved });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The full search form is gone (TanTable handles search + filtering); the only
  // server-side filters that remain are the Show Ended / Revisions toggles in the
  // header. Changing one updates the form and immediately re-runs the search.
  const handleTopFilterChange = (patch: Partial<QuotesSearchForm>) => {
    const next = { ...form, ...patch };
    setForm(next);
    // Show Ended is remembered per user so the page reopens as left.
    if (patch.showEnded !== undefined) {
      void userSettingsService.saveUserSetting(SHOW_ENDED_SETTING_KEY, { ended: patch.showEnded });
    }
    void runSearch({ form: next });
  };

  const applyCustomFilter = (blob: string, name?: string) => {
    setCustomFilter(blob);
    setCustomFilterName(name);
    setFilterOpen(false);
    runSearch({ form, customFilter: blob, customFilterName: name });
  };

  const clearCustomFilter = () => {
    setCustomFilter('');
    setCustomFilterName(undefined);
    runSearch({ form, customFilter: '', customFilterName: undefined });
  };

  const applyCustomSearch = (blob: string, ended: boolean) => {
    setCustomSearch(blob);
    setSearchEnded(ended);
    setSearchOpen(false);
    // Persist the toggle so it reopens as last used.
    void userSettingsService.saveUserSetting(SEARCH_ENDED_SETTING_KEY, { ended });
    // "Search Ended" forces the search to include (or exclude) ended quotes,
    // overriding the view's normal Show Ended / Active Only rule.
    runSearch({ form, customSearch: blob, showEnded: ended });
  };

  const clearCustomSearch = () => {
    setCustomSearch('');
    runSearch({ form, customSearch: '' });
  };

  // Quote field registry: display names for the active-filter chips AND the
  // source for the flat-view dynamic column picker (always loaded; the hook
  // module-caches so it's a one-time fetch).
  const { fieldDefinitions: quoteFieldDefs, loading: quoteFieldDefsLoading } = useQuoteFieldDefinitions(true);

  // Picker-eligible quote fields: displayable, NOT already a fixed flat-grid column
  // (avoids duplicate picker entries), and one the backend can generate as a flat
  // column — either a computed sql_expression field (no join_key) or a supported
  // single-valued join_key (excludes notes/charges 1:many and quote_search_*
  // duplicates). Mirrors quotes.py `_quote_extra_select` (`fd.sql_expression or
  // fd.join_key in _QUOTE_COLUMN_JOIN_KEYS`).
  const quoteColumnDefs = useMemo(
    () => quoteFieldDefs.filter(
      (d) => d.is_displayable
        && !QUOTE_FLAT_FIXED_FIELDNAMES.has(d.fieldname)
        && (!!d.sql_expression || (!!d.join_key && SUPPORTED_QUOTE_COLUMN_JOIN_KEYS.has(d.join_key))),
    ),
    [quoteFieldDefs],
  );

  // Add/remove a flat-view dynamic column: update state, persist the choice as a
  // user setting (so it reloads as left), and refetch so the new field's data is
  // included in the (grouped) query's lanes.
  const handleAdditionalFieldsChange = useCallback((next: string[]) => {
    setAdditionalFields(next);
    // Wrap in an object — the user-settings API body must be a dict, not a bare array.
    void userSettingsService.saveUserSetting(FLAT_COLUMNS_SETTING_KEY, { fields: next });
    void runSearch({ additionalFields: next });
  }, [runSearch]);

  // Independent banners — both can show (filter AND search active simultaneously).
  const filterBanner = useMemo(() => {
    if (!customFilter) return null;
    return {
      kind: 'filter' as const,
      title: customFilterName ? `Filter: ${customFilterName}` : 'Custom filter',
      chips: summarizeFilter(customFilter, quoteFieldDefs),
      onEdit: () => setFilterOpen(true),
    };
  }, [customFilter, customFilterName, quoteFieldDefs]);

  const searchBanner = useMemo(() => {
    if (!customSearch) return null;
    const d = describeSearchBlob(customSearch);
    return {
      kind: 'search' as const,
      title: 'Custom search',
      chips: d ? [`${d.label}: ${d.values}`] : [],
      onEdit: () => setSearchOpen(true),
    };
  }, [customSearch]);


  return (
    <div className="flex flex-col min-h-screen bg-[#f5f6f8] dark:bg-gray-900">
      <main className="flex-1 min-w-0 flex flex-col">
        <QuotesPageHeader
          onCreateQuote={() => setCreateOpen(true)}
          onRateCalculator={() => setCalcOpen(true)}
          onCustomFilter={() => setFilterOpen(true)}
          onCustomSearch={() => setSearchOpen(true)}
          customFilterActive={!!customFilter}
          customSearchActive={!!customSearch}
        />

        <div className="flex flex-col gap-3.5 px-[22px] pt-3.5 pb-8">
          {filterBanner && <QuotesActiveFilterBanner {...filterBanner} onClear={clearCustomFilter} />}
          {searchBanner && <QuotesActiveFilterBanner {...searchBanner} onClear={clearCustomSearch} />}
          {/* Tracks which quotes you've opened this session (green pill + a ring
              on the most recently opened) via the flat view's Quote # column. */}
          <ViewedProsProvider>
            <QuotesTable
              groups={groups}
              totalGroups={totalGroups}
              flatLanes={flatLanes}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              onOpenQuote={handleOpenQuote}
              loading={loading}
              error={error}
              showEnded={form.showEnded}
              allRevisions={form.revisions === 'all'}
              includeFuture={form.includeFuture}
              activeOnly={activeOnly}
              onActiveOnlyChange={handleActiveOnlyChange}
              onShowEndedChange={(v) => handleTopFilterChange({ showEnded: v })}
              onAllRevisionsChange={(v) => handleTopFilterChange({ revisions: v ? 'all' : 'current' })}
              onIncludeFutureChange={(v) => handleTopFilterChange({ includeFuture: v })}
              quoteFieldDefs={quoteColumnDefs}
              additionalFields={additionalFields}
              onAdditionalFieldsChange={handleAdditionalFieldsChange}
              // Gate on BOTH async loads the flat grid's columns depend on: the
              // persisted dynamic-column field list (flatFieldsLoaded) AND the
              // field-definition registry (quoteFieldDefsLoading) that turns a
              // field name into an actual column def. Cold-session race: if the
              // list resolves first, the grid would mount missing the not-yet-
              // defined column, drop it from the restored saved order, then a
              // later reconciliation appends it to the end and autosaves —
              // permanently overwriting the user's real layout.
              flatFieldsReady={flatFieldsLoaded && !quoteFieldDefsLoading}
            />
          </ViewedProsProvider>
        </div>
      </main>

      {/* FLAT view-icon opens the per-quote editor drawer in place (no nav) for
          DIRECT quotes only — WAREHOUSE/other quotes navigate to the full editor
          (the drawer is DirectTab-only). On save / revision / dupe it re-runs the
          current search so the flattened rows reflect the change. `key` resets
          per-lane editor state on switch. */}
      <QuoteLaneEditorDrawer
        key={openLane?.quoteId}
        open={!!openLane}
        lane={openLane}
        onClose={() => setOpenLane(null)}
        onChanged={() => { void runSearch({ form }); }}
      />

      {/* New Quote reuses the SAME editor drawer in create mode (unified UX) —
          a blank quote opens looking exactly like an existing one. */}
      <QuoteLaneEditorDrawer
        key="new-quote"
        open={createOpen}
        lane={null}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onChanged={() => { void runSearch({ form }); }}
      />
      {/* Read-only rate lookup — replaces the create-a-mock-quote-and-save
          workaround the quote team used to read a lane's Rate. */}
      <RateCalculatorModal open={calcOpen} onClose={() => setCalcOpen(false)} />
      <CustomFilterDialog
        open={filterOpen}
        initialBlob={customFilter}
        onClose={() => setFilterOpen(false)}
        onApply={applyCustomFilter}
      />
      <CustomSearchDialog
        open={searchOpen}
        initialBlob={customSearch}
        initialSearchEnded={searchEnded}
        onClose={() => setSearchOpen(false)}
        onApply={applyCustomSearch}
      />
    </div>
  );
};

export default QuotesPage;
